import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { supabase } from '../../lib/supabase';
import { formatTime, EVENTS } from '../../lib/vdot';
import { checkQualifiers } from '../../lib/qualStandards';

export function ProAthletes() {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debugErr, setDebugErr] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadProAthletes();
  }, []);

  async function loadProAthletes() {
    setLoading(true);

    // Fetch pro athletes via SECURITY DEFINER RPC (bypasses RLS for admin)
    const { data: proRows, error: rpcErr } = await supabase.rpc('get_pro_athletes');
    if (rpcErr) {
      setDebugErr(`RPC error: ${rpcErr.message} (code: ${rpcErr.code})`);
      setAthletes([]);
      setLoading(false);
      return;
    }

    if (!proRows || proRows.length === 0) {
      setAthletes([]);
      setLoading(false);
      return;
    }

    const ids = proRows.map(p => p.id);

    // Load latest benchmark per athlete
    const { data: allBenchmarks } = await supabase
      .from('benchmarks')
      .select('athlete_id, event, time_seconds, race_date')
      .in('athlete_id', ids)
      .order('race_date', { ascending: false });

    // Load pending gear request counts
    const { data: gearCounts } = await supabase
      .from('pro_gear_requests')
      .select('athlete_id, status')
      .in('athlete_id', ids)
      .eq('status', 'pending');

    // Load coach names (coaches are readable since the admin is a coach too)
    const coachIds = [...new Set(proRows.map(p => p.coach_id).filter(Boolean))];
    const coachMap = {};
    if (coachIds.length > 0) {
      const { data: coaches } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', coachIds);
      (coaches ?? []).forEach(c => { coachMap[c.id] = c.full_name; });
    }

    const gearCountMap = {};
    (gearCounts ?? []).forEach(g => {
      gearCountMap[g.athlete_id] = (gearCountMap[g.athlete_id] ?? 0) + 1;
    });

    // Group benchmarks per athlete for qualifier check
    const bmByAthlete = {};
    (allBenchmarks ?? []).forEach(b => {
      if (!bmByAthlete[b.athlete_id]) bmByAthlete[b.athlete_id] = [];
      bmByAthlete[b.athlete_id].push(b);
    });

    const result = proRows.map(ap => {
      const bmarks = bmByAthlete[ap.id] ?? [];
      const latestBm = bmarks[0] ?? null;
      const quals = ap.gender ? checkQualifiers(bmarks, ap.gender) : [];

      return {
        id: ap.id,
        full_name: ap.full_name ?? 'Unknown',
        email: ap.email ?? '',
        avatar_url: null,
        gender: ap.gender,
        archived: ap.archived ?? false,
        coachName: ap.coach_id ? (coachMap[ap.coach_id] ?? null) : null,
        latestBenchmark: latestBm,
        pendingGearCount: gearCountMap[ap.id] ?? 0,
        hasComments: !!(ap.pro_comments && ap.pro_comments.trim().length > 0),
        qualifierCount: quals.length,
      };
    });

    // Sort by name
    result.sort((a, b) => a.full_name.localeCompare(b.full_name));
    setAthletes(result);
    setLoading(false);
  }

  function initials(name) {
    if (!name) return '?';
    return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
  }

  const activeAthletes   = athletes.filter(a => !a.archived);
  const archivedAthletes = athletes.filter(a => a.archived);
  const displayAthletes  = showArchived ? archivedAthletes : activeAthletes;

  return (
    <Layout>
      <div style={{ marginBottom: 24 }}>
        <p className="page-eyebrow">Admin</p>
        <h1 className="page-title">Pro Athletes</h1>
        <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--gray-400)' }}>
          Athletes with Apex Elite Pro status
        </p>
      </div>

      {loading ? (
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      ) : athletes.length === 0 ? (
        <div className="section-card">
          <div className="section-card-body">
            {debugErr ? (
              <div style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 12, fontSize: 13, fontFamily: 'monospace' }}>
                <strong>Debug:</strong> {debugErr}
              </div>
            ) : null}
            No pro athletes found. Set an athlete&apos;s grade_level to &apos;pro&apos; to add them here.
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn-xs btn-xs-ghost" onClick={() => setShowArchived(v => !v)}>
              {showArchived
                ? `← Active Athletes (${activeAthletes.length})`
                : `View Archived (${archivedAthletes.length})`}
            </button>
          </div>
          <div className="section-card">
          <div className="section-card-header">
            <span className="section-card-title">{showArchived ? `Archived Athletes (${archivedAthletes.length})` : `Pro Roster (${activeAthletes.length})`}</span>
          </div>
          {displayAthletes.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">{showArchived ? 'No archived pro athletes' : 'No active pro athletes'}</p>
            </div>
          ) : displayAthletes.map(a => (
            <div
              key={a.id}
              onClick={() => navigate(`/admin/pro-athletes/${a.id}`)}
              style={{
                padding: '14px 24px', borderBottom: '1px solid var(--gray-100)',
                display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--gray-50)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = ''; }}
            >
              {/* Avatar */}
              <div className="profile-avatar" style={{ width: 44, height: 44, fontSize: 14, flexShrink: 0, cursor: 'pointer' }}>
                {a.avatar_url
                  ? <img src={a.avatar_url} alt={a.full_name} />
                  : initials(a.full_name)
                }
              </div>

              {/* Name / email / coach */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {a.full_name}
                  <span className="badge badge-gold" style={{ fontSize: 9, letterSpacing: '0.08em', padding: '2px 7px' }}>PRO</span>
                  {a.archived && <span className="admin-plan-badge admin-plan-badge--missing" style={{ fontSize: 10 }}>Archived</span>}
                  {a.qualifierCount > 0 && (
                    <span style={{ fontSize: 11, background: 'rgba(252,198,37,0.2)', color: '#92400e', borderRadius: 999, padding: '2px 8px', fontWeight: 700 }}>
                      &#127942; {a.qualifierCount} Qual{a.qualifierCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 1, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span>{a.email}</span>
                  {a.coachName && <span>&middot; Coach {a.coachName.split(' ').slice(-1)[0]}</span>}
                  {a.gender && <span>&middot; {a.gender.charAt(0).toUpperCase() + a.gender.slice(1)}</span>}
                </div>
              </div>

              {/* Latest benchmark */}
              <div style={{ textAlign: 'right', minWidth: 120 }}>
                {a.latestBenchmark ? (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>
                      {formatTime(a.latestBenchmark.time_seconds)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                      {EVENTS[a.latestBenchmark.event]?.label ?? a.latestBenchmark.event}
                    </div>
                  </>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>No benchmarks</span>
                )}
              </div>

              {/* Badges */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {a.pendingGearCount > 0 && (
                  <span style={{ background: '#fef9c3', color: '#92400e', borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 700 }}>
                    {a.pendingGearCount} Gear
                  </span>
                )}
                {a.hasComments && (
                  <span style={{ background: 'var(--gray-100)', color: 'var(--gray-500)', borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 600 }}>
                    Comment
                  </span>
                )}
              </div>

              {/* Chevron */}
              <div style={{ color: 'var(--gray-400)', fontSize: 18, flexShrink: 0 }}>&rsaquo;</div>
            </div>
          ))}
          </div>
        </>
      )}
    </Layout>
  );
}
